// import { createWithEqualityFn } from "zustand/traditional"; // causing problem with testing
import {
  StateStorage,
  createJSONStorage,
  devtools,
  persist,
  subscribeWithSelector,
} from "zustand/middleware";

import { logger } from "./middlewares";
import { createSelectors } from "./utils";

import { StateCreator, create } from "zustand";

import { isDevelopment } from "../utils";
import { createAuthStore, type TAuthStore } from "./auth";
import {
  TBreadCrumbStore,
  createBreadcrumbSlice,
} from "./breadcrumb/createBreadcrumbSlice";
import { CreateCommonSlice, TCommonStore } from "./common/CreateCommonSlice";
import {
  createEvaluationDetailSlice,
  type TEvaluationDetailStore,
} from "./evaluation-details/createEvaluationDetailSlice";
import {
  CreateDataIngestionSlice,
  TDataIngestionStore,
} from "./data-ingestion/CreateDataIngestionSlice";
import {
  CreateManageTemplateSlice,
  TManageTemplateStore,
} from "./manage-template/CreateManageTemplateSlice";
import {
  createAgentPerformanceSlice,
  TAgentPerformanceStore,
} from "./agent-performance/createAgentPerformanceSlice";
import {
  createNotificationSlice,
  type TNotificationStore,
} from "./notifications/CreateNotificationSlice";
import { STORE_VERSION } from "@/core/const";
import {
  createOrganizationHierarchySlice,
  TOrganizationHierarchyStore,
} from "./organization-hierarchy/createOrganizationHierarchySlice";
import {
  CreateBrandingSlice,
  TBrandingStore,
} from "./branding/CreateBrandingSlice";
import {
  TTeamPerfReportsStore,
  createTeamPerformanceReportsSlice,
} from "./team-performance-reports/createTeamPerformanceReportsSlice";
import {
  createEvaluationSlice,
  TEvaluationStore,
} from "./evaluation/createEvaluationSlice";

export type TStore = TAuthStore &
  TBreadCrumbStore &
  TEvaluationDetailStore &
  TCommonStore &
  TDataIngestionStore &
  TManageTemplateStore &
  TAgentPerformanceStore &
  TNotificationStore &
  TOrganizationHierarchyStore &
  TBrandingStore &
  TTeamPerfReportsStore &
  TEvaluationStore;

// NOTE: must keep unique keys for each store slice
export const store: StateCreator<TStore> = (...args) => {
  return {
    ...createAuthStore(...args),
    ...createEvaluationDetailSlice(...args),
    ...createBreadcrumbSlice(...args),
    ...CreateCommonSlice(...args),
    ...CreateDataIngestionSlice(...args),
    ...CreateManageTemplateSlice(...args),
    ...createAgentPerformanceSlice(...args),
    ...createNotificationSlice(...args),
    ...createOrganizationHierarchySlice(...args),
    ...CreateBrandingSlice(...args),
    ...createTeamPerformanceReportsSlice(...args),
    ...createEvaluationSlice(...args),
  } as TStore;
};

const isDebugMode =
  isDevelopment && Boolean(import.meta.env.VITE_VITEST_ENV) === false;
export const STORE_NAME = "appStore";
export const useStoreBase = create<TStore>()(
  logger(
    devtools(
      // to subscribe with a selector, now useStore.subscribe accepts addtional signature
      // subscribe(selector, callback, options?: { equalityFn, fireImmediately }): Unsubscribe
      subscribeWithSelector(
        // READ-MORE: https://docs.pmnd.rs/zustand/integrations/persisting-store-data
        persist(store, {
          // required, (unique)
          name: STORE_NAME,
          // (optional) by default the 'localStorage' is used. Custom storage function(implementing StateStorage type) can also be used.
          storage: createJSONStorage(() => localStorage as StateStorage),
          // old persisted version of the store will be dumped and new persisted store will be used if version doesn't match.
          // NOTE: use migrate function to handle breaking changes in order to persist previously stored data.
          version: STORE_VERSION,
          // enables us to pick specific state's fields to be stored in the storage
          partialize: (state) => ({
            featureShowHideFlag: state.featureShowHideFlag,
          }),
          // Convert Array back to Set when hydrating
          merge: (persistedState, currentState) => {
            const persisted = persistedState as Partial<TStore>;
            return {
              ...currentState,
              ...persisted,
            };
          },
          migrate: (persistedState, prevVersion) => {
            if (prevVersion !== STORE_VERSION) {
              // handle breaking changes here
              // persistedState.newField = persistedState.oldField
              // delete persistedState.oldField

              return persistedState;
            }
            return persistedState;
          },
        }),
      ),
      { enabled: isDebugMode },
    ),
    {
      name: STORE_NAME,
      enabled: isDebugMode,
    },
  ),
);

export const clearStore = () => useStoreBase.persist.clearStorage();

export const useStore = createSelectors(useStoreBase);
